// F1-3 垂直单图
import { FC, useState } from 'react'
import clsx from 'clsx'
import PdMedia from '@/components/pd-media/pd-media'
import { AspectRatio, IVerticalSingleMedia, Theme, paddingTopClassMap, paddingBottomClassMap } from '@/common/pd-meta-components-types'
import SlidingTabs from '@/components/standard/pd-sliding-tabs'
import useScrollScale from '@/hooks/tools/use-scroll-scale'
import TagItemRightIcon from '../../tag-item-right-icon'
import PdTextMatchSup from '@/components/pd-text-match-sup/pd-text-match-sup'
import { useMobileDetector } from '@/hooks/use-mobile-detector'
import SemanticText from '@/components/standard/semantic-text'

const aspectRatioClass: Record<AspectRatio, string> = {
  [AspectRatio.OneToOne]: '[aspect-ratio:1/1]',
  [AspectRatio.OneToTwo]: '[aspect-ratio:1/2]',
  [AspectRatio.TwoToOne]: '[aspect-ratio:2/1]',
  [AspectRatio.TwoToThree]: '[aspect-ratio:2/3]',
  [AspectRatio.ThreeToTwo]: '[aspect-ratio:3/2]',
  [AspectRatio.ThreeToFour]: '[aspect-ratio:3/4]',
  [AspectRatio.FourToThree]: '[aspect-ratio:4/3]',
  [AspectRatio.NineToSixteen]: '[aspect-ratio:9/16]',
  [AspectRatio.SixteenToNine]: '[aspect-ratio:16/9]',
}

const VerticalSingleMedia: FC<IVerticalSingleMedia> = ({
  theme = Theme.Dark,
  title,
  titleTag,
  titleClassName,
  titleSupClassName,
  backgroundClassName = 'bg-container-primary',
  desc,
  tagGroup = [],
  mediaGroup = [],
  mediaAspectRatio = AspectRatio.TwoToOne,
  mediaAspectRatioH5,
  mediaBorderRadius = 0,
  paddingTop = 'px120',
  paddingBottom = 'px120',
}) => {
  const [activeTabKey, setActiveTabKey] = useState<number>(0)
  const isMobile = useMobileDetector()
  const currentAspectRatio = isMobile && mediaAspectRatioH5 ? mediaAspectRatioH5 : mediaAspectRatio
  const isSingleInfo = tagGroup && tagGroup?.length === 1

  const containerRef = useScrollScale({
    targetSelector: '.target-f1-3',
  })

  return (
    <div
      className={clsx(
        'w-full h-fit',
        paddingTopClassMap[paddingTop],
        paddingBottomClassMap[paddingBottom],
        `pd-theme-${theme}`,
        backgroundClassName
      )}
    >
      <div className={clsx('')}>
        {(title || desc || (tagGroup && tagGroup?.length > 0)) && (
          <div className={clsx('mb-8 max-w-300 mx-auto laptop:mb-64px', 'px-6 laptop:px-0')}>
            {/* title */}
            {title && (
              <SemanticText
                as={titleTag}
                defaultTag="h3"
                className={clsx(
                  'font-semibold',
                  'mb-3 laptop:mb-6',
                  'laptop:text-center',
                  'whitespace-pre-wrap break-words',
                  'text-fc-primary pd-first-title',
                  titleSupClassName && '[&>span]:inline-flex [&>span]:items-start',
                  titleClassName
                )}
              >
                <PdTextMatchSup text={title} supClassName={titleSupClassName} />
              </SemanticText>
            )}
            <div className={clsx('flex flex-col')}>
              {(isSingleInfo || desc) && (
                <div className={clsx(isSingleInfo && 'flex gap-x-30 flex-col-reverse gap-y-8 laptop:flex-row')}>
                  {isSingleInfo && (
                    <>
                      {tagGroup.map((item) => {
                        return <TagItemRightIcon key={item.title} {...item} />
                      })}
                    </>
                  )}
                  {desc && (
                    <p
                      className={clsx(
                        'laptop:text-center',
                        'laptop:w-180 mx-auto',
                        'whitespace-pre-wrap break-words',
                        'text-fc-secondary pd-main-text'
                      )}
                    >
                      {desc}
                    </p>
                  )}
                </div>
              )}
              {tagGroup && tagGroup.length > 1 && (
                <div
                  className={clsx(
                    'flex flex-row flex-wrap justify-start',
                    // tagGroup.length > 4 ? 'laptop:justify-start' : 'laptop:justify-center',
                    'laptop:justify-center',
                    'gap-y-8 laptop:gap-y-16',
                    'laptop:gap-x-16',
                    // 'laptop:gap-x-16',
                    'mt-8 laptop:mt-16',
                    'text-fc-primary'
                  )}
                >
                  {tagGroup.map((item) => {
                    return (
                      <div key={item.title} className="shrink-0 w-1/2 laptop:w-fit">
                        <TagItemRightIcon {...item} />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="w-full laptop:max-w-300 mx-auto" ref={containerRef}>
          <SlidingTabs
            contentBoxClassName={clsx(
              `w-full h-auto laptop:h-fit ${aspectRatioClass[currentAspectRatio] || aspectRatioClass['3/2']}`
            )}
            tabsBoxClassName={clsx('pb-3 laptop:pb-6 flex justify-center')}
            tabsContainerClassName="laptop:rounded-full"
            containerClassName={clsx('w-full flex flex-col-reverse gap-y-3 laptop:gap-y-6')}
            tabLabels={mediaGroup.map((item) => item.tabTitle || '')}
            bgColor="bg-container-third"
            tabEleClassName="rounded-full hover:bg-white/20 active:bg-black/20"
            activeTabChange={(key) => {
              setActiveTabKey(key)
            }}
          >
            {mediaGroup.map((item) => {
              return (
                <div
                  key={item.tabTitle}
                  className={clsx(`w-full h-full laptop:h-fit overflow-hidden`)}
                  style={{
                    aspectRatio: currentAspectRatio,
                    borderRadius: `${mediaBorderRadius}px`,
                  }}
                >
                  <PdMedia
                    useLoader={true}
                    h5Src={item.mediaH5Url}
                    src={item.mediaUrl || ''}
                    mask={false}
                    className={`target-f1-3 scale-150`}
                  />
                </div>
              )
            })}
          </SlidingTabs>
          <p className={clsx('text-16', 'laptop:text-18', 'text-center', 'text-fc-secondary')}>
            {mediaGroup[activeTabKey]?.desc}
          </p>
        </div>
      </div>
    </div>
  )
}

export default VerticalSingleMedia
